import { ARTICLE_TAG_COLORS } from '@application/constants';
import { useRouter } from '@application/utils';
import type { ArticlesGetArticlesQuery } from '@gql/graphql';
import type { CardItemArticleData } from '@presentation/components';
import { BREAKPOINTS } from '@presentation/styles';
import { useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';

export const useFormatArticles = (
  articles?: ArticlesGetArticlesQuery['findManyArticle'] | null,
) => {
  const { routeLocationDescriptor, translate, infoFromCurrentPath } = useRouter();
  const isMobile = useMediaQuery({ query: `(max-width: ${BREAKPOINTS.phone}px)` });
  const preloadImagesCount = isMobile ? 1 : 4;

  const formattedArticles: CardItemArticleData[] | undefined = useMemo(() => {
    if (!articles || !articles.length) return;
    return articles.map(
      (article, index) =>
        ({
          title: article.translations?.[0]?.title,
          image: article.coverImg,
          reviewed: article.reviewed,
          tags: article.tags.map((tag) => ({
            label: translate(`common.articleTags.${tag}`),
            color: ARTICLE_TAG_COLORS[tag],
          })),
          link: routeLocationDescriptor('article', {
            params: [article.translations?.[0]?.url],
            state: {
              returnSearch: infoFromCurrentPath.search,
            },
          }),
          lazyLoadImage: index >= preloadImagesCount,
        } as CardItemArticleData),
    );
  }, [
    articles,
    infoFromCurrentPath.search,
    preloadImagesCount,
    routeLocationDescriptor,
    translate,
  ]);

  const imagesToPreload: string[] = useMemo(() => {
    const headerImage = 'https://tipaw.com/assets/tipaw-blog-header-DRJGSJLO.webp';

    if (!formattedArticles?.length) return [headerImage];
    const images = formattedArticles
      .slice(0, preloadImagesCount)
      .map((article) => article.image as string);

    return [...images, headerImage];
  }, [formattedArticles, preloadImagesCount]);

  return { formattedArticles, imagesToPreload };
};
